import { Modal, unstable_composeClasses as composeClasses } from '@mui/base';
import { alpha, Backdrop, styled, useThemeProps } from '@mui/material';
import clsx from 'clsx';
import { forwardRef, useCallback, useMemo } from 'react';
import useAutoclose from '../useAutoclose';
import { capitalize, rootShouldForwardProp } from '../utils';
import { getFeedbackNotificationUtilityClass } from './feedbackNotificationClasses';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export const getFeedbackNotificationSlots = ownerState => {
  const {
    color
  } = ownerState;
  return {
    root: ['root', `color${capitalize(color)}`],
    notification: ['notification'],
    icon: ['icon'],
    message: ['message']
  };
};
const useUtilityClasses = ownerState => {
  const {
    classes
  } = ownerState;
  const slots = getFeedbackNotificationSlots(ownerState);
  const composedClasses = composeClasses(slots, getFeedbackNotificationUtilityClass, classes);
  return {
    ...classes,
    ...composedClasses
  };
};
const FeedbackNotificationRoot = styled('div', {
  name: 'MuiOriFeedbackNotification',
  slot: 'Root',
  overridesResolver: ({
    ownerState
  }, styles) => [styles.root, styles[`color${capitalize(ownerState.color)}`]],
  shouldForwardProp: prop => rootShouldForwardProp(prop)
})(({
  theme
}) => ({
  position: 'fixed',
  zIndex: theme.zIndex.modal,
  right: 0,
  bottom: 0,
  top: 0,
  left: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  outline: 0
}));
const FeedbackNotificationBackdrop = styled(Backdrop, {
  name: 'MuiOriFeedbackNotification',
  slot: 'Backdrop',
  overridesResolver: (_, styles) => [styles.backdrop]
})({
  zIndex: -1
});
const Notification = styled('div', {
  name: 'MuiOriFeedbackNotification',
  slot: 'Notification',
  skipSx: true,
  overridesResolver: (_, styles) => [styles.notification]
})(({
  theme,
  ownerState
}) => ({
  display: 'flex',
  backdropFilter: 'blur(2px)',
  outline: 0,
  flexDirection: 'row',
  justifyItems: 'center',
  alignItems: 'flex-start',
  columnGap: theme.spacing(2),
  padding: theme.spacing(3),
  minHeight: 'unset',
  borderRadius: theme.shape.largeComponentRadius,
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(6)
  },
  ...(ownerState.color === 'error' && {
    backgroundColor: alpha(theme.palette.error.main, 0.85),
    color: theme.palette.error.contrastText
  }),
  ...(ownerState.color === 'success' && {
    backgroundColor: alpha(theme.palette.success.main, 0.85),
    color: theme.palette.success.contrastText
  }),
  ...(ownerState.color === 'warning' && {
    backgroundColor: alpha(theme.palette.warning.main, 0.85),
    color: theme.palette.warning.contrastText
  }),
  ...(ownerState.color === 'initial' && {
    backgroundColor: alpha(theme.palette.grey['900'], 0.8),
    color: theme.palette.common.white
  })
}));
const Message = styled('div', {
  name: 'MuiOriFeedbackNotification',
  slot: 'Message',
  skipSx: true,
  overridesResolver: (_, styles) => [styles.message]
})(({
  theme,
  ownerState
}) => ({
  fontSize: theme.typography.body1.fontSize,
  fontWeight: 'bold',
  alignSelf: 'center',
  order: 2,
  display: 'flex',
  alignItems: 'flex-start',
  maxWidth: 250,
  '&>p': {
    fontSize: 'inherit',
    fontWeight: 'inherit'
  },
  [theme.breakpoints.up('md')]: {
    maxWidth: 350
  }
}));
const IconRoot = styled('div', {
  name: 'MuiOriFeedbackNotification',
  slot: 'Icon',
  skipSx: true,
  overridesResolver: (_, styles) => [styles.icon]
})(({
  theme,
  ownerState
}) => ({
  fontSize: '3rem',
  lineHeight: 1,
  '&>svg': {
    fontSize: 'inherit'
  }
}));
export const FeedbackNotification = /*#__PURE__*/forwardRef((inProps, ref) => {
  const props = useThemeProps({
    props: inProps,
    name: 'MuiOriFeedbackNotification'
  });
  const {
    children,
    className,
    closeAfterTransition = true,
    color = 'initial',
    disableAutoFocus = false,
    disableEnforceFocus = false,
    disableEscapeKeyDown = false,
    disablePortal = true,
    disableRestoreFocus = false,
    disableScrollLock = true,
    hideBackdrop = true,
    keepMounted = false,
    icon,
    open,
    onClose,
    timeoutMs = 2000,
    ...other
  } = props;
  const commonProps = {
    closeAfterTransition,
    disableAutoFocus,
    disableEnforceFocus,
    disableEscapeKeyDown,
    disablePortal,
    disableRestoreFocus,
    disableScrollLock,
    hideBackdrop,
    keepMounted
  };
  const ownerState = useMemo(() => ({
    ...props,
    color
  }), [color, props]);
  const classes = useUtilityClasses(ownerState);
  const handleClose = useCallback(() => {
    onClose?.({}, 'backdropClick');
  }, [onClose]);
  useAutoclose({
    onClose: handleClose,
    open,
    timeoutMs,
    autoClose: true
  });
  return /*#__PURE__*/_jsx(Modal, {
    slots: {
      root: FeedbackNotificationRoot,
      backdrop: FeedbackNotificationBackdrop
    },
    slotProps: {
      root: () => ({
        ownerState
      })
    },
    open: open,
    className: clsx(classes.root, className),
    ...other,
    ref: ref,
    ...commonProps,
    children: /*#__PURE__*/_jsxs(Notification, {
      className: classes.notification,
      ownerState: ownerState,
      children: [/*#__PURE__*/_jsx(IconRoot, {
        ownerState: ownerState,
        className: classes.icon,
        children: icon
      }), /*#__PURE__*/_jsx(Message, {
        ownerState: ownerState,
        className: classes.message,
        children: children
      })]
    })
  });
});
export default FeedbackNotification;
//# sourceMappingURL=FeedbackNotification.js.map